(() => {
  'use strict';

  const initialize = () => {
    const root = document.documentElement;
    const body = document.body;
    if (!body || root.hasAttribute('data-rudca-widget-clearance-ready')) return;
    root.setAttribute('data-rudca-widget-clearance-ready', '');

    const toolsSelector = '[data-rudca-cart-widget-tools]';
    const rewardsSelector = '[data-rudca-open-rewards]';
    const launcherSelector = '#appstle-loyalty-button';
    const panelSelector = '#appstle_loyalty_iframe';
    const protectedSelector = '#mini-cart[aria-hidden="false"], #rudca-quantity-dialog[open], .pswp[aria-hidden="false"]';
    const watchedSelector = `#mini-cart, #rudca-quantity-dialog, .pswp, ${toolsSelector}, ${rewardsSelector}, ${launcherSelector}, ${panelSelector}`;
    let chatVisible = false;
    let rewardsVisible = location.hash === '#appstle-loyalty';
    let rewardsOpener = null;
    let panelWasOpen = false;

    const getProvider = () => {
      const button = document.querySelector(launcherSelector);
      const panel = document.querySelector(panelSelector);
      return button instanceof HTMLButtonElement && !button.disabled && panel instanceof HTMLIFrameElement ? { button, panel } : null;
    };

    const sync = () => {
      const tools = document.querySelector(toolsSelector);
      const managedCart = body.classList.contains('template-cart') && Boolean(tools);
      const protectedSurfaceOpen = Boolean(document.querySelector(protectedSelector));
      const provider = getProvider();
      const replacementReady = Boolean(document.querySelector('[data-rudca-rewards-menu="mobile"]') &&
        document.querySelector('[data-rudca-rewards-menu="inline"], [data-rudca-rewards-menu="dropdown"]'));
      root.toggleAttribute('data-rudca-rewards-in-menu', Boolean(provider && replacementReady));

      const panelOpen = Boolean(provider?.panel.classList.contains('open'));
      if (panelWasOpen && !panelOpen) {
        rewardsVisible = false;
        if (rewardsOpener?.isConnected && !protectedSurfaceOpen) rewardsOpener.focus({ preventScroll: true });
        rewardsOpener = null;
      }
      panelWasOpen = panelOpen;

      const chatPaused = protectedSurfaceOpen || (managedCart && !chatVisible);
      const rewardsPaused = protectedSurfaceOpen || (managedCart && !rewardsVisible);
      root.toggleAttribute('data-rudca-chat-paused', chatPaused);
      root.toggleAttribute('data-rudca-rewards-paused', rewardsPaused);
      root.toggleAttribute('data-rudca-widgets-paused', chatPaused && rewardsPaused);
      document.querySelectorAll(rewardsSelector).forEach((control) => {
        if (provider) {
          control.setAttribute('aria-controls', provider.panel.id);
          control.setAttribute('aria-expanded', String(panelOpen && !rewardsPaused));
        } else {
          control.removeAttribute('aria-controls');
          control.removeAttribute('aria-expanded');
        }
      });

      if (!tools) return;
      const show = tools.querySelector('[data-rudca-show-widgets]');
      const hide = tools.querySelector('[data-rudca-hide-widgets]');
      if (show) {
        show.hidden = !managedCart;
        const expanded = managedCart && chatVisible && !protectedSurfaceOpen;
        show.setAttribute('aria-expanded', String(expanded));
        const label = show.getAttribute(expanded ? 'data-rudca-hide-label' : 'data-rudca-show-label');
        if (label && show.textContent !== label) show.textContent = label;
      }
      if (hide) hide.hidden = true;
    };

    // The header delegates to Shopify's existing launcher only after this
    // clearance layer has made chat available on the full-cart page.
    document.addEventListener('rudca:chat:request-open', () => {
      if (document.querySelector(protectedSelector)) return;
      chatVisible = true;
      sync();
    });
    document.addEventListener('rudca:chat:closed', () => {
      chatVisible = false;
      sync();
    });

    const returnToCart = () => {
      chatVisible = false;
      rewardsVisible = false;
      rewardsOpener = null;
      const provider = getProvider();
      if (provider?.panel.classList.contains('open')) provider.button.click();
      sync();
      const target = document.querySelector('.cart-recap [name="checkout"]') ||
        document.querySelector(`${toolsSelector} [data-rudca-show-widgets]`);
      target?.focus({ preventScroll: true });
    };

    const openRewards = (control) => {
      const provider = getProvider();
      if (!provider || document.querySelector(protectedSelector)) return false;
      rewardsOpener = control;
      if (control.closest('#mobile-menu')) {
        const toggle = document.querySelector('[aria-controls="mobile-menu"][data-action="toggle-menu"]');
        if (toggle?.getAttribute('aria-expanded') === 'true') {
          rewardsOpener = toggle;
          toggle.click();
        }
      } else if (control.closest('#desktop-menu-0')) {
        const toggle = document.querySelector('.header__desktop-nav-toggle[data-action="toggle-menu"]');
        if (toggle?.getAttribute('aria-expanded') === 'true') {
          rewardsOpener = toggle;
          toggle.click();
        }
      }
      rewardsVisible = true;
      sync();
      if (!provider.panel.classList.contains('open')) provider.button.click();
      sync();
      if (provider.panel.classList.contains('open')) provider.panel.focus({ preventScroll: true });
      return true;
    };

    document.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const rewards = target?.closest(rewardsSelector);
      if (rewards && event.button === 0 && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
        if (openRewards(rewards)) event.preventDefault();
        return;
      }
      const control = target?.closest('[data-rudca-show-widgets], [data-rudca-hide-widgets]');
      if (!control || !control.closest(toolsSelector)) return;
      if (control.hasAttribute('data-rudca-hide-widgets')) returnToCart();
      else {
        // A ready header adapter opens the same native conversation directly.
        // Cancellation means it handled this click; otherwise keep the native
        // show/hide fallback, including when the provider has not loaded yet.
        const request = new CustomEvent('rudca:chat:toggle', { cancelable: true, detail: { control } });
        if (!document.dispatchEvent(request)) return;
        chatVisible = !chatVisible;
        sync();
        control.focus({ preventScroll: true });
      }
    });

    const observer = new MutationObserver((records) => {
      const needsSync = records.some((record) => {
        if (record.type === 'attributes') return record.target.matches(`#mini-cart, #rudca-quantity-dialog, .pswp, ${panelSelector}, ${launcherSelector}`);
        return [...record.addedNodes, ...record.removedNodes].some((node) =>
          node instanceof Element && (node.matches(watchedSelector) || node.querySelector(watchedSelector))
        );
      });
      if (needsSync) sync();
    });
    observer.observe(body, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-hidden', 'open', 'class', 'disabled'] });
    window.addEventListener('pageshow', () => {
      chatVisible = false;
      rewardsVisible = location.hash === '#appstle-loyalty';
      rewardsOpener = null;
      sync();
    });
    document.addEventListener('cart:rerendered', sync, true);
    document.addEventListener('shopify:section:load', sync);
    sync();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();

(() => {
  'use strict';

  const initialize = () => {
    const root = document.documentElement;
    if (!document.body || root.hasAttribute('data-rudca-chat-clearance-ready')) return;
    root.setAttribute('data-rudca-chat-clearance-ready', '');

    const marker = 'data-rudca-chat-action-overlap';
    const actionSelector = '[data-action="add-to-cart"], [data-rudca-product-add], [data-rudca-basket-change], [data-rudca-quantity-minus], [data-rudca-quantity-plus], .quantity-selector__button, .quantity-selector__value, .product-form__single-selector, .product-form select[name="id"], .product-form .color-swatch__item, .product-form .variant-swatch__item, .product-form .block-swatch__item, .product-item .color-swatch__item, .product-item a.rudca-card-add, .rudca-product-price .price, .product-item .price, #mobile-menu';
    const scopeSelector = `shopify-chat, .product-item, .product-form, [data-rudca-basket-scope], ${actionSelector}`;
    const scrollIdleDelay = 250;
    let frame = 0;
    let scrollTimer = 0;
    let scrolling = false;
    let pageHidden = false;
    const observed = new Set();
    const schedule = () => {
      if (!pageHidden && !frame) frame = requestAnimationFrame(sync);
    };
    const resetScroll = () => {
      clearTimeout(scrollTimer);
      scrollTimer = 0;
      scrolling = false;
    };
    const onScroll = () => {
      if (pageHidden) return;
      scrolling = true;
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(() => {
        scrollTimer = 0;
        scrolling = false;
        schedule();
      }, scrollIdleDelay);
      schedule();
    };
    const resizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;

    const intersection = (a, b) => {
      const rect = { left: Math.max(a.left, b.left), top: Math.max(a.top, b.top), right: Math.min(a.right, b.right), bottom: Math.min(a.bottom, b.bottom) };
      return rect.right > rect.left && rect.bottom > rect.top ? rect : null;
    };

    const visibleRect = (element, viewport, ignoreOwnVisibility = false, clipAncestors = false) => {
      let rect = intersection(element.getBoundingClientRect(), viewport);
      if (!rect) return null;
      for (let node = element; node; node = node.parentElement || node.getRootNode().host) {
        const style = getComputedStyle(node);
        if (style.display === 'none' || Number(style.opacity) === 0 ||
          (!(node === element && ignoreOwnVisibility) && (style.visibility === 'hidden' || style.visibility === 'collapse'))) return null;
        if (clipAncestors && node !== element && /hidden|clip|auto|scroll/.test(`${style.overflowX} ${style.overflowY}`)) {
          const bounds = node.getBoundingClientRect();
          rect = intersection(rect, {
            left: /hidden|clip|auto|scroll/.test(style.overflowX) ? bounds.left : rect.left,
            right: /hidden|clip|auto|scroll/.test(style.overflowX) ? bounds.right : rect.right,
            top: /hidden|clip|auto|scroll/.test(style.overflowY) ? bounds.top : rect.top,
            bottom: /hidden|clip|auto|scroll/.test(style.overflowY) ? bounds.bottom : rect.bottom
          });
          if (!rect) return null;
        }
      }
      return rect;
    };

    const sync = () => {
      frame = 0;
      const visual = window.visualViewport;
      const viewport = { left: visual?.offsetLeft || 0, top: visual?.offsetTop || 0 };
      viewport.right = viewport.left + (visual?.width || root.clientWidth || innerWidth);
      viewport.bottom = viewport.top + (visual?.height || root.clientHeight || innerHeight);
      const actions = [...document.querySelectorAll(actionSelector)];
      const nextObserved = new Set([document.body, ...actions]);
      const changes = [...document.querySelectorAll('shopify-chat')].map((host) => {
        nextObserved.add(host);
        const runtime = host.shadowRoot?.querySelector('shopify-agent-runtime[exportparts]');
        const exported = runtime?.getAttribute('exportparts').split(',').map((part) => part.trim());
        const activator = runtime?.shadowRoot?.querySelector('button[part~="activator"]');
        const dialog = runtime?.shadowRoot?.querySelector('[part~="dialog"]');
        let overlap = false;
        if (exported?.includes('activator') && exported.includes('dialog') && activator && dialog) {
          nextObserved.add(activator);
          nextObserved.add(dialog);
          const focused = runtime.shadowRoot.activeElement === activator || activator.contains(runtime.shadowRoot.activeElement);
          const launcherRect = visibleRect(activator, viewport, host.hasAttribute(marker));
          if (!focused && launcherRect && !visibleRect(dialog, viewport)) {
            overlap = scrolling || actions.some((action) => intersection(action.getBoundingClientRect(), launcherRect) &&
              Boolean(visibleRect(action, launcherRect, false, true)));
          }
        }
        return { host, overlap };
      });
      changes.forEach(({ host, overlap }) => {
        if (host.hasAttribute(marker) !== overlap) host.toggleAttribute(marker, overlap);
      });
      if (resizeObserver) {
        observed.forEach((element) => {
          if (!nextObserved.has(element)) { resizeObserver.unobserve(element); observed.delete(element); }
        });
        nextObserved.forEach((element) => {
          if (!observed.has(element)) { resizeObserver.observe(element); observed.add(element); }
        });
      }
    };

    const observer = new MutationObserver((records) => {
      if (records.some((record) => record.type === 'attributes'
        ? record.target.matches(scopeSelector) || Boolean(record.target.closest('.product-item, .product-form, [data-rudca-basket-scope]')) || Boolean(record.target.querySelector(actionSelector))
        : [...record.addedNodes, ...record.removedNodes].some((node) => node instanceof Element &&
          (node.matches(scopeSelector) || node.querySelector(scopeSelector))))) schedule();
    });
    observer.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['hidden', 'aria-hidden', 'class', 'style', 'view', 'open'] });
    ['transitionrun', 'transitionend', 'transitioncancel'].forEach((type) => {
      document.addEventListener(type, (event) => {
        if (!(event.target instanceof Element) || !event.target.matches('#mobile-menu')) return;
        schedule();
        if (type === 'transitionrun') requestAnimationFrame(schedule);
      }, true);
    });
    document.addEventListener('scroll', onScroll, { capture: true, passive: true });
    document.addEventListener('click', schedule, true);
    document.addEventListener('focusin', schedule);
    document.addEventListener('focusout', schedule);
    document.addEventListener('load', schedule, true);
    document.addEventListener('cart:rerendered', schedule, true);
    document.addEventListener('shopify:section:load', schedule);
    window.addEventListener('resize', schedule, { passive: true });
    window.addEventListener('pagehide', () => {
      pageHidden = true;
      resetScroll();
      cancelAnimationFrame(frame);
      frame = 0;
    });
    window.addEventListener('pageshow', () => {
      pageHidden = false;
      resetScroll();
      schedule();
    });
    window.visualViewport?.addEventListener('resize', schedule, { passive: true });
    window.visualViewport?.addEventListener('scroll', onScroll, { passive: true });
    if (window.customElements) {
      ['shopify-chat', 'shopify-agent-runtime'].forEach((name) => customElements.whenDefined(name).then(schedule));
    }
    schedule();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
